import React, { useId } from "react";

/**
 * RangeSlider — slider personalizado con relleno de color visible
 * @param {number}   value
 * @param {function} onChange   - recibe el nuevo número
 * @param {number}   min
 * @param {number}   max
 * @param {number}   step
 * @param {string}   label
 * @param {boolean}  showValue  - muestra el valor actual a la derecha del label
 * @param {string}   valueLabel - formateo del valor (ej. "%" o "€")
 * @param {"primary"|"secondary"|"accent"|"success"|"warning"|"error"} color
 * @param {boolean}  disabled
 */
export default function RangeSlider({
    value = 0,
    onChange,
    min = 0,
    max = 100,
    step = 1,
    label,
    showValue = true,
    valueLabel = "",
    color = "primary",
    disabled = false,
    marks,           // array de strings: ["0%","25%","50%","75%","100%"]
}) {
    const id = useId();
    const percent = ((value - min) / (max - min)) * 100;

    const colorMap = {
        primary:   { fill: "bg-primary",   thumb: "bg-primary",   ring: "focus-visible:ring-primary" },
        secondary: { fill: "bg-secondary", thumb: "bg-secondary", ring: "focus-visible:ring-secondary" },
        accent:    { fill: "bg-accent",    thumb: "bg-accent",    ring: "focus-visible:ring-accent" },
        success:   { fill: "bg-success",   thumb: "bg-success",   ring: "focus-visible:ring-success" },
        warning:   { fill: "bg-warning",   thumb: "bg-warning",   ring: "focus-visible:ring-warning" },
        error:     { fill: "bg-error",     thumb: "bg-error",     ring: "focus-visible:ring-error" },
    };

    const c = colorMap[color] ?? colorMap.primary;

    return (
        <div className={`flex flex-col gap-2 w-full ${disabled ? "opacity-40" : ""}`}>
            {(label || showValue) && (
                <div className="flex items-center justify-between">
                    {label && (
                        <label htmlFor={id} className="text-sm font-semibold dark:text-gray-200 text-gray-700">
                            {label}
                        </label>
                    )}
                    {showValue && (
                        <span className={`text-sm font-bold ${colorMap[color]?.fill.replace("bg-", "text-") ?? "text-primary"}`}>
                            {value}{valueLabel}
                        </span>
                    )}
                </div>
            )}

            {/* Track container */}
            <div className="relative flex items-center h-6">
                {/* Track base */}
                <div className="absolute w-full h-2 rounded-full bg-base-300" />

                {/* Track fill */}
                <div
                    className={`absolute h-2 rounded-full ${c.fill} transition-all`}
                    style={{ width: `${percent}%` }}
                />

                {/* Native input (invisible, handles drag events) */}
                <input
                    id={id}
                    type="range"
                    min={min}
                    max={max}
                    step={step}
                    value={value}
                    disabled={disabled}
                    onChange={e => onChange?.(Number(e.target.value))}
                    className={`
                        absolute w-full h-full opacity-0 cursor-pointer
                        ${disabled ? "cursor-not-allowed" : ""}
                    `}
                />

                {/* Custom thumb */}
                <div
                    className={`
                        absolute w-5 h-5 rounded-full ${c.thumb} border-2 border-white dark:border-base-200
                        shadow-md pointer-events-none transition-all
                    `}
                    style={{ left: `calc(${percent}% - 10px)` }}
                />
            </div>

            {/* Marks */}
            {marks && (
                <div className="flex justify-between text-xs text-gray-400 dark:text-gray-500 px-0.5">
                    {marks.map(m => <span key={m}>{m}</span>)}
                </div>
            )}
        </div>
    );
}
